/**
 * Shared rendering for universal-search results — a grouped, keyboard-navigable
 * list used by both the Quick Search modal and the inline Dashboard search
 * dropdown. Groups come from buildResultGroups(); `active` is the flat index of
 * the highlighted row (for ↑/↓ nav).
 */

import { useEffect, useState } from 'react';
import * as remoteTools from '@/api/endpoints/remoteTools.js';

// key → { agent, ts }. Live agent resolution for device rows, shared across
// both search surfaces and throttled so re-renders/keystrokes don't spam.
const liveStatusCache = new Map();
const LIVE_TTL_MS = 30_000;

/**
 * Resolve a device's LIVE agent EXACTLY like Asset Management's useResolvedAgent:
 * /remote-tools/resolve with intuneDeviceId → serial → hostname (intuneDeviceId
 * is the only reliable key for Android, whose android-<serial> hostname often
 * uses a different serial than Intune). Returns { state, hostname }; falls back
 * to the search-cache snapshot until it resolves. Also writes the resolved
 * hostname back onto the record so activation opens the correct agent.
 */
function useLiveAgentState(item) {
  const isDevice = item.kind === 'device';
  const r = item.record || {};
  const [resolved, setResolved] = useState(undefined); // undefined=loading, null=none, {online,hostname}
  useEffect(() => {
    if (!isDevice) return undefined;
    if (!r.name && !r.serialNumber && !r.intuneDeviceId) { setResolved(null); return undefined; }
    const key = `${r.name}|${r.serialNumber}|${r.intuneDeviceId}`;
    const cached = liveStatusCache.get(key);
    if (cached && Date.now() - cached.ts < LIVE_TTL_MS) { setResolved(cached.agent); applyResolved(r, cached.agent); return undefined; }
    let cancelled = false;
    remoteTools.resolveRemoteAgent({ hostname: r.name, serial: r.serialNumber, intuneDeviceId: r.intuneDeviceId })
      .then((agent) => {
        const a = agent ?? null;
        liveStatusCache.set(key, { agent: a, ts: Date.now() });
        applyResolved(r, a);
        if (!cancelled) setResolved(a);
      })
      .catch(() => { if (!cancelled) setResolved(null); });
    return () => { cancelled = true; };
  }, [isDevice, r.name, r.serialNumber, r.intuneDeviceId]);

  if (!isDevice) return item.agentState;
  if (resolved === undefined) return item.agentState; // snapshot until live resolves
  if (!resolved) return 'none';
  return resolved.online ? 'online' : 'offline';
}

/** Write the resolved agent's real hostname back so activation opens it. */
function applyResolved(record, agent) {
  if (!record) return;
  if (agent?.hostname) { record.agentHostname = agent.hostname; record.hasAgent = true; }
  else if (agent === null) { record.hasAgent = false; }
}

/** Small agent-online indicator for device rows. */
function AgentDot({ state }) {
  if (!state) return null;
  const map = {
    online: { color: '#10b981', label: 'Agent online' },
    offline: { color: '#9ca3af', label: 'Agent offline' },
    none: { color: 'var(--faint)', label: 'No agent' }
  };
  const m = map[state] ?? map.none;
  return (
    <span title={m.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: m.color, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: m.color, display: 'inline-block' }} />
      {m.label}
    </span>
  );
}

export function SearchResultRow({ item, meta, active, onMouseEnter, onClick }) {
  // Devices with an enrolled agent activate straight into Remote Tools.
  const isDevice = item.kind === 'device';
  const agentState = useLiveAgentState(item);
  const hasAgent = isDevice && (agentState === 'online' || agentState === 'offline');
  const actionLabel = hasAgent ? 'Remote Tools' : 'Open';
  return (
    <div
      role="button"
      tabIndex={-1}
      onMouseEnter={onMouseEnter}
      onMouseDown={(e) => e.preventDefault()} /* keep input focus so blur doesn't beat the click */
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '9px 14px',
        borderRadius: 12, cursor: 'pointer',
        background: active ? 'var(--accent-soft)' : 'transparent',
        transition: 'background 0.12s ease'
      }}
    >
      <div style={{
        width: 34, height: 34, minWidth: 34, borderRadius: 10, background: meta.gradient,
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14
      }}>
        <i className={`fas ${meta.icon}`} aria-hidden="true" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
        {item.sub && <div style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.sub}</div>}
      </div>
      {isDevice && <AgentDot state={agentState} />}
      <span style={{
        fontSize: 10.5, fontWeight: 800, padding: '3px 9px', borderRadius: 999,
        background: hasAgent ? 'rgba(16,185,129,0.14)' : 'var(--accent-soft)',
        color: hasAgent ? '#059669' : 'var(--accent)', letterSpacing: '0.03em', whiteSpace: 'nowrap'
      }}>
        {hasAgent && <i className="fas fa-desktop" style={{ fontSize: 9, marginRight: 4 }} aria-hidden="true" />}
        {actionLabel} <i className="fas fa-arrow-right" style={{ fontSize: 9 }} aria-hidden="true" />
      </span>
    </div>
  );
}

/**
 * @param {{ groups: any[], active: number, onHover: (idx:number)=>void,
 *           onActivate: (item:any)=>void }} props
 */
export default function SearchResultsList({ groups, active, onHover, onActivate }) {
  let flatIndex = -1;
  return groups.map((g) => (
    <div key={g.kind} style={{ marginBottom: 6 }}>
      <div style={{
        padding: '8px 14px 4px', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: 'var(--faint)'
      }}>
        {g.meta.label} · {g.all.length}
      </div>
      {g.shown.map((it, i) => {
        flatIndex += 1;
        const idx = flatIndex;
        return (
          <SearchResultRow
            key={`${g.kind}-${i}`}
            item={it}
            meta={g.meta}
            active={idx === active}
            onMouseEnter={() => onHover(idx)}
            onClick={() => onActivate(it)}
          />
        );
      })}
      {g.all.length > g.shown.length && (
        <div style={{ padding: '4px 14px 6px', fontSize: 12, color: 'var(--faint)' }}>
          +{g.all.length - g.shown.length} more — refine your search
        </div>
      )}
    </div>
  ));
}
